import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../components/Button";
import PageHeader from "../components/PageHeader";
import { API_ENDPOINTS } from "../config/api";
import { REASK_MODEL_OPTIONS } from "../config/providerModels";
import {
  MessagesResponseTabs,
  AdminResponseTabs,
} from "../components/ResponseTabs";
import { Citations, processMessageContent } from "../components/Citations";
import { formatMessageTime, formatDateWithTime } from "../utils/dateFormatting";

// Styled Components
const MessagesContainer = styled.div`
  background: #f8f9fa;
  height: ${({ $isEmbedded }) => ($isEmbedded ? "100%" : "100vh")};
  min-height: ${({ $isEmbedded }) => ($isEmbedded ? "100%" : "100vh")};
  flex: ${({ $isEmbedded }) => ($isEmbedded ? "1" : "none")};
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "80px")};
  position: ${({ $isEmbedded }) => ($isEmbedded ? "absolute" : "relative")};
  top: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "auto")};
  left: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "auto")};
  right: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "auto")};
  bottom: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "auto")};

  @media (max-width: 768px) {
    padding-top: 0; /* Remove padding on mobile since header is relative */
  }
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: ${({ $isEmbedded }) => ($isEmbedded ? "100%" : "calc(100% - 78px)")};
  position: relative;
`;

const MessagesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ $isEmbedded }) =>
    $isEmbedded ? "2rem 1rem 140px 1rem" : "2rem 0"};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    padding: ${({ $isEmbedded }) =>
      $isEmbedded ? "1rem 0 120px 0" : "1rem 0 120px 0"};
  }

  @media (max-width: 480px) {
    padding: ${({ $isEmbedded }) =>
      $isEmbedded ? "1rem 0.5rem 140px 0.5rem" : "1rem 0.5rem 140px 0.5rem"};
  }
`;

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: ${({ $isEmbedded }) => ($isEmbedded ? "100%" : "60rem")};
  width: 100%;
  padding: 0 1rem;

  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 70%;
  align-self: ${({ $type }) => ($type === "user" ? "flex-end" : "flex-start")};
  align-items: ${({ $type }) => ($type === "user" ? "flex-end" : "flex-start")};

  @media (max-width: 768px) {
    max-width: 85%;
  }

  @media (max-width: 480px) {
    max-width: 95%;
  }
`;

const MessageContent = styled.div`
  min-height: 50px;
  background: ${({ $type }) => {
    if ($type === "user") return "#000000";
    if ($type === "bot") return "#e5e5e5";
    if ($type === "error") return "#ffebee";
    return "#f1f3f4";
  }};
  color: ${({ $type }) => {
    if ($type === "user") return "white";
    if ($type === "error") return "#c62828";
    return "#333";
  }};
  padding: 0.75rem;
  border-radius: 1rem;
  position: relative;
  width: fit-content;
`;

const MessageText = styled.div`
  p {
    margin: 0 0 0.5rem 0;
    line-height: 1.375;
    word-wrap: break-word;
    font-size: 1.125rem;
  }

  p:last-of-type {
    margin-bottom: 0;
  }

  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: decimal;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: disc;
  }

  li {
    margin: 0.25rem 0;
    line-height: 1.4;
    word-wrap: break-word;
  }

  ol li {
    margin: 0.5rem 0;
  }

  ul li {
    margin: 0.25rem 0;
  }

  ol ol,
  ul ul,
  ol ul,
  ul ol {
    margin: 0.25rem 0;
    padding-left: 1.2rem;
  }

  strong {
    font-weight: 600;
    color: inherit;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.75rem 0 0.5rem 0;
    font-weight: 600;
    line-height: 1.3;
  }

  h1 {
    font-size: 1.5rem;
  }
  h2 {
    font-size: 1.375rem;
  }
  h3 {
    font-size: 1.25rem;
  }
`;

const MessageMetadata = styled.span`
  font-size: 0.875rem;
  opacity: 0.7;
  display: block;
  margin-top: 0.5rem;
  padding: 0 0.5rem;
  color: #666;
  white-space: nowrap;
  width: fit-content;
  align-self: ${({ $type }) => ($type === "user" ? "flex-end" : "flex-start")};
`;

const RatingContainer = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border: 0.125rem solid #e9ecef;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: fit-content;
  max-width: 100%;
  color: #6c757d;
  font-size: 0.875rem;
`;

const SavedFeedbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const SavedFeedbackDisplay = styled.div`
  width: 20rem;
  min-height: 4rem;
  padding: 0.5rem;
  background: #ffffff;
  border: 0.125rem solid #e9ecef;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const RatingButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const RatingButton = styled.button`
  background: ${({ $selected, $type }) => {
    if ($selected && $type === "up") return "#d4edda";
    if ($selected && $type === "down") return "#f8d7da";
    return "transparent";
  }};
  border: 0.125rem solid
    ${({ $selected, $type }) => {
      if ($selected && $type === "up") return "#28a745";
      if ($selected && $type === "down") return "#dc3545";
      return "#dee2e6";
    }};
  color: ${({ $selected, $type }) => {
    if ($selected && $type === "up") return "#155724";
    if ($selected && $type === "down") return "#721c24";
    return "#6c757d";
  }};
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  height: 2rem;
  width: 3rem;
  font-size: 1rem;

  &:hover {
    background: ${({ $type }) => ($type === "up" ? "#d4edda" : "#f8d7da")};
    border-color: ${({ $type }) => ($type === "up" ? "#28a745" : "#dc3545")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeedbackButton = styled.button`
  background: transparent;
  border: 0.125rem solid #dee2e6;
  color: #6c757d;
  border-radius: 0.25rem;
  padding: 0.25rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  height: 2rem;
  width: fit-content !important;

  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
`;

const FeedbackInput = styled.textarea`
  width: 20rem;
  min-height: 4rem;
  padding: 0.5rem;
  border: 0.125rem solid #dee2e6;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const FeedbackActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const FeedbackActionButton = styled.button`
  background: ${({ $primary }) => ($primary ? "#000000" : "transparent")};
  color: ${({ $primary }) => ($primary ? "white" : "#6c757d")};
  border: 0.125rem solid ${({ $primary }) => ($primary ? "#000000" : "#dee2e6")};
  border-radius: 0.25rem;
  padding: 0.25rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    background: ${({ $primary }) => ($primary ? "#000000" : "#e9ecef")};
    border-color: ${({ $primary }) => ($primary ? "#000000" : "#adb5bd")};
  }
`;

const typingAnimation = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  min-height: 1.5rem;
  padding: 0 0.5rem;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #000000;
    animation: ${typingAnimation} 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
`;

const MessageInputContainer = styled.div`
  position: ${({ $isEmbedded }) => ($isEmbedded ? "absolute" : "fixed")};
  bottom: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "0")};
  left: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "0")};
  right: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "0")};
  padding: ${({ $isEmbedded }) => ($isEmbedded ? "2rem" : "1rem")};
  background: #f8f9fa;
  border-top: 0.125rem solid #e9ecef;
  z-index: ${({ $isEmbedded }) => ($isEmbedded ? "auto" : "100")};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  width: ${({ $isEmbedded }) => ($isEmbedded ? "100%" : "calc(100% - 300px)")};

  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const MessageInputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  max-width: ${({ $isEmbedded }) =>
    $isEmbedded ? "100%" : "calc(60rem - 2rem)"};
  margin: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "0 auto")};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    max-width: 100%;
    margin: 0;
  }
`;

const FileInputButton = styled.button`
  background: transparent;
  border: 2px solid #e1e5e9;
  border-radius: 0.75rem;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #f8f9fa;
    border-color: #dee2e6;
    color: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectedFilesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const SelectedFileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.625rem;
  background: ${({ $hasError }) => ($hasError ? "#f8d7da" : "#e9ecef")};
  border: 1px solid ${({ $hasError }) => ($hasError ? "#f5c6cb" : "transparent")};
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: ${({ $hasError }) => ($hasError ? "#721c24" : "#495057")};
  max-width: 250px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${({ $hasError }) => ($hasError ? "#721c24" : "#6c757d")};
    display: flex;
    align-items: center;
    padding: 0;
    font-size: 1rem;
    line-height: 1;
    flex-shrink: 0;

    &:hover {
      color: #dc3545;
    }
  }
`;

const FileErrorText = styled.span`
  font-size: 0.7rem;
  color: #dc3545;
  margin-left: 0.25rem;
`;

const FileCountBadge = styled.span`
  font-size: 0.75rem;
  color: #6c757d;
  margin-left: 0.25rem;
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: 0.5125rem 0.5rem;
  border: 2px solid #e1e5e9;
  border-radius: 0.75rem;
  font-size: 1.125rem;
  resize: none;
  min-height: 44px;
  max-height: 512px;
  width: 100%;
  overflow-y: auto;
  height: auto;

  &:focus {
    outline: none;
    border-color: #000000;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

// Mobile responsive wrapper
const MobileWrapper = styled.div`
  @media (max-width: 768px) {
    ${ChatContainer} {
      height: calc(100vh - 78px);
    }
  }
`;

// Sidebar styled components
const SidePanel = styled.div`
  width: 300px;
  background: white;
  border-left: 0.125rem solid #e0e0e0;
  padding: 1.5rem;
  overflow-y: auto;
  height: calc(100vh - 80px);
  position: fixed;
  right: 0;
  top: 80px;
  z-index: 100;
  display: ${({ $isEmbedded }) => ($isEmbedded ? "none" : "block")};

  @media (max-width: 1024px) {
    display: none;
  }
`;

const MainContent = styled.div`
  margin-right: ${({ $isEmbedded }) => ($isEmbedded ? "0" : "300px")};

  @media (max-width: 1024px) {
    margin-right: 0;
  }
`;

const ThreadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ThreadItem = styled.div`
  padding: 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background: ${({ $active }) => ($active ? "#f0f0f0" : "transparent")};
  border: ${({ $active }) =>
    $active ? "0.125rem solid #000000" : "0.125rem solid transparent"};

  &:hover {
    background: ${({ $active }) => ($active ? "#f0f0f0" : "#f8f9fa")};
  }
`;

const ThreadDate = styled.span`
  color: #666;
  font-size: 0.75rem;
`;

const ThreadTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ThreadTitle = styled.span`
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const EditButton = styled.button`
  background: transparent;
  border: none;
  padding: 0.125rem;
  cursor: pointer;
  color: #999;
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }

  ${ThreadTitleRow}:hover & {
    opacity: 1;
  }
`;

const ThreadTitleInput = styled.input`
  width: 100%;
  padding: 0.25rem;
  border: 1px solid #000;
  border-radius: 0.25rem;
  font-size: inherit;
  font-weight: 500;
  color: #333;

  &:focus {
    outline: none;
    border-color: #000;
  }
`;

// Helper function to extract response content from partial JSON more aggressively
const extractResponseFromPartialJson = (content) => {
  // Try multiple strategies to extract clean response content

  // Strip markdown code fences (Claude 4.x wraps JSON in ```json ... ```)
  content = content.replace(/^```(?:json)?\s*\n?/, "").replace(/\n?```\s*$/, "");

  // Strategy 1: Handle double-encoded JSON (for re-ask responses)
  // Look for content that starts with {\" and try to unescape it first
  if (content.trim().startsWith('{\\"')) {
    try {
      // Try to unescape the outer layer of JSON encoding
      const unescapedContent = content
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, "\\");

      // Now try to parse this as regular JSON
      const parsed = JSON.parse(unescapedContent);
      if (parsed.response) {
        return parsed.response;
      }
    } catch (parseError) {
      // If that fails, continue with other strategies using the unescaped content
      const unescapedContent = content
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, "\\");

      // Try the regex approach on the unescaped content
      const responseMatch = unescapedContent.match(
        /"response"\s*:\s*"((?:[^"\\]|\\.)*)"/,
      );
      if (responseMatch) {
        return responseMatch[1]
          .replace(/\\\\/g, "\\")
          .replace(/\\"/g, '"')
          .replace(/\\n/g, "\n")
          .replace(/\\r/g, "\r")
          .replace(/\\t/g, "\t");
      }

      // Try incomplete match on unescaped content
      const incompleteMatch = unescapedContent.match(
        /"response"\s*:\s*"((?:[^"\\]|\\.)*)/,
      );
      if (incompleteMatch) {
        let responseText = incompleteMatch[1]
          .replace(/\\\\/g, "\\")
          .replace(/\\"/g, '"')
          .replace(/\\n/g, "\n")
          .replace(/\\r/g, "\r")
          .replace(/\\t/g, "\t");

        // Remove trailing quote if present
        responseText = responseText.replace(/"$/, "");
        return responseText;
      }
    }
  }

  // Strategy 2: Look for complete "response":"..." field (standard case)
  const responseMatch = content.match(/"response"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (responseMatch) {
    let responseText = responseMatch[1]
      .replace(/\\\\/g, "\\") // Handle double backslashes first
      .replace(/\\"/g, '"')
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t");

    // If we detect that citations are starting but incomplete, truncate at that point
    if (
      content.includes('"citations"') &&
      !content.match(/"citations"\s*:\s*\[.*\]/)
    ) {
      // Find where citations start and cut off the response there
      const citationsStart = content.indexOf('"citations"');
      if (citationsStart > -1) {
        // Re-extract response up to where citations begin, but exclude trailing comma/whitespace
        const contentBeforeCitations = content.substring(0, citationsStart);
        const truncatedMatch = contentBeforeCitations.match(
          /"response"\s*:\s*"((?:[^"\\]|\\.)*)"/,
        );
        if (truncatedMatch) {
          responseText = truncatedMatch[1]
            .replace(/\\\\/g, "\\")
            .replace(/\\"/g, '"')
            .replace(/\\n/g, "\n")
            .replace(/\\r/g, "\r")
            .replace(/\\t/g, "\t");
        }
      }
    }

    return responseText;
  }

  // Strategy 3: Look for response field that might be incomplete (missing closing quote)
  const incompleteMatch = content.match(/"response"\s*:\s*"((?:[^"\\]|\\.)*)/);
  if (incompleteMatch) {
    let responseText = incompleteMatch[1]
      .replace(/\\\\/g, "\\") // Handle double backslashes first
      .replace(/\\"/g, '"')
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t");

    // Check if we're hitting the start of citations and should stop
    // Look for patterns like '",\n  "citations"' or the end of the response field
    if (
      content.includes('"citations"') ||
      content.includes('\\"citations\\"')
    ) {
      // If we can find the complete response field, extract just the content
      const completeResponseMatch = content.match(
        /"response"\s*:\s*"((?:[^"\\]|\\.)*)"/,
      );
      if (completeResponseMatch) {
        responseText = completeResponseMatch[1]
          .replace(/\\\\/g, "\\")
          .replace(/\\"/g, '"')
          .replace(/\\n/g, "\n")
          .replace(/\\r/g, "\r")
          .replace(/\\t/g, "\t");
      } else {
        // Remove any trailing quote that might be part of the JSON structure
        responseText = responseText.replace(/"$/, "");
      }
    }

    return responseText;
  }

  // Strategy 4: If content looks like it might be starting JSON, try to find any text content
  if (content.trim().startsWith("{")) {
    // Look for any text that might be response content after escaping
    const textMatch = content.match(/"([^"]*(?:\\.[^"]*)*)"/);
    if (textMatch) {
      const extracted = textMatch[1]
        .replace(/\\\\/g, "\\") // Handle double backslashes first
        .replace(/\\"/g, '"')
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r")
        .replace(/\\t/g, "\t");

      // Only return if it looks like actual response content (not just field names)
      if (
        extracted &&
        !extracted.match(
          /^(response|citations|confidence|id|source|content|relevance_score)$/,
        )
      ) {
        return extracted;
      }
    }
  }

  // Strategy 5: Fall back to original content but clean it up if it's clearly JSON
  if (content.trim().startsWith('{"response"')) {
    // If it looks like raw JSON, show a placeholder instead
    return ""; // Empty content rather than raw JSON
  }

  // Final fallback - return original content but still clean up escape sequences if present
  // But avoid showing partial citations or trailing commas
  let cleanContent = content
    .replace(/\\\\/g, "\\") // Handle double backslashes
    .replace(/\\n/g, "\n") // Convert \n to actual newlines
    .replace(/\\"/g, '"') // Convert \" to quotes
    .replace(/\\r/g, "\r") // Convert \r to carriage returns
    .replace(/\\t/g, "\t"); // Convert \t to tabs

  // If content contains partial citations, truncate before them (and any trailing comma)
  if (
    (cleanContent.includes('"citations"') ||
      cleanContent.includes('\\"citations\\"')) &&
    !cleanContent.match(/"citations"\s*:\s*\[.*\]/)
  ) {
    const citationsIndex = Math.min(
      cleanContent.indexOf('"citations"') !== -1
        ? cleanContent.indexOf('"citations"')
        : Infinity,
      cleanContent.indexOf('\\"citations\\"') !== -1
        ? cleanContent.indexOf('\\"citations\\"')
        : Infinity,
    );
    if (citationsIndex < Infinity) {
      cleanContent = cleanContent.substring(0, citationsIndex);
      // Remove any trailing comma, quote, and whitespace
      cleanContent = cleanContent.replace(/[,"\s]*$/, "").trim();
    }
  }

  return cleanContent;
};

// Helper function to parse citations from raw content
const parseContentAndCitations = (content) => {
  // Try to detect and extract JSON data at the end of the content

  // Pattern 1: Content starting with {"response": - Full JSON object at the beginning
  if (
    content.startsWith('{"response":') ||
    content.startsWith('{ "response":')
  ) {
    try {
      const fullData = JSON.parse(content);
      if (fullData.response) {
        return {
          content: fullData.response,
          citations: fullData.citations
            ? JSON.stringify(fullData.citations)
            : null,
        };
      }
    } catch (e) {
      // If full JSON parse fails, try regex extraction
      const responseMatch = content.match(
        /"response"\s*:\s*"((?:[^"\\]|\\.)*)"/,
      );
      const citationsMatch = content.match(/"citations"\s*:\s*(\[.*?\])/s);

      if (responseMatch) {
        return {
          content: responseMatch[1]
            .replace(/\\\\/g, "\\")
            .replace(/\\"/g, '"')
            .replace(/\\n/g, "\n")
            .replace(/\\r/g, "\r")
            .replace(/\\t/g, "\t"),
          citations: citationsMatch ? citationsMatch[1] : null,
        };
      }
    }
  }

  // Pattern 2: Content ending with .", "citations": [...]
  let citationsPattern = /^(.*?)",\s*"citations"\s*:\s*(\[.*?\])/s;
  let match = content.match(citationsPattern);

  if (match) {
    return {
      content: match[1],
      citations: match[2],
    };
  }

  // Pattern 3: Full JSON object { "response": "...", "citations": [...], "confidence": ... } (not starting with {"response":)
  if (content.includes('"citations":') && content.includes('"confidence":')) {
    try {
      const fullData = JSON.parse(content);
      if (fullData.response && fullData.citations) {
        return {
          content: fullData.response,
          citations: JSON.stringify(fullData.citations),
        };
      }
    } catch (e) {
      // If full JSON parse fails, try regex extraction
      const responseMatch = content.match(
        /"response"\s*:\s*"((?:[^"\\]|\\.)*)"/,
      );
      const citationsMatch = content.match(/"citations"\s*:\s*(\[.*?\])/s);

      if (responseMatch && citationsMatch) {
        return {
          content: responseMatch[1]
            .replace(/\\\\/g, "\\")
            .replace(/\\"/g, '"')
            .replace(/\\n/g, "\n")
            .replace(/\\r/g, "\r")
            .replace(/\\t/g, "\t"),
          citations: citationsMatch[1],
        };
      }
    }
  }

  // Pattern 4: Content with escaped JSON citations
  if (content.includes('\\", \\"citations\\":')) {
    const citationsIndex = content.indexOf('", "citations":');
    if (citationsIndex > -1) {
      const extractedContent = content.substring(0, citationsIndex);
      const remainingContent = content.substring(citationsIndex);
      const citationsMatch = remainingContent.match(
        /"citations"\s*:\s*(\[.*?\])/s,
      );

      if (citationsMatch) {
        return {
          content: extractedContent,
          citations: citationsMatch[1],
        };
      }
    }
  }

  // Pattern 5: Any content ending with JSON-like structures
  if (
    content.includes('"citations"') &&
    (content.includes('"url":') ||
      content.includes('"relevance_score":') ||
      content.includes('"confidence":'))
  ) {
    // Try to find where the actual content ends and JSON begins
    // Look for patterns like: text.", "citations": or text." } or similar
    const patterns = [
      /^(.*?)",\s*"citations"\s*:\s*(\[.*?\])/s,
      /^(.*?)\.",\s*"citations"\s*:\s*(\[.*?\])/s,
      /^(.*?)\."[^"]*$/s,
    ];

    for (const pattern of patterns) {
      const match = content.match(pattern);
      if (match) {
        const citationsMatch = content.match(/"citations"\s*:\s*(\[.*?\])/s);
        return {
          content: match[1],
          citations: citationsMatch ? citationsMatch[1] : null,
        };
      }
    }
  }

  // No pattern matched, return original content
  return {
    content: content,
    citations: null,
  };
};

function Messages({
  user,
  selectedBot,
  token,
  onBack,
  onLogout,
  isEmbedded = false,
}) {
  const { threadId: urlThreadId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(urlThreadId || null);
  const [activeThreadId, setActiveThreadId] = useState(urlThreadId || null);
  const [showFeedbackFor, setShowFeedbackFor] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [activeTabs, setActiveTabs] = useState({}); // Track active tab for each question group
  const [messageHeights, setMessageHeights] = useState({}); // Track message heights during re-ask
  const [messageWidths, setMessageWidths] = useState({}); // Track message widths during re-ask
  const [threads, setThreads] = useState([]); // List of user's threads
  const [isViewingLoadedThread, setIsViewingLoadedThread] =
    useState(!!urlThreadId); // Track if viewing a loaded thread
  const [editingThreadId, setEditingThreadId] = useState(null); // Track which thread is being renamed
  const [editingTitle, setEditingTitle] = useState(""); // Track the title being edited
  const messageContentRefs = useRef({}); // Store refs for message content elements
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null); // Ref to the scrollable container
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const skipThreadLoadRef = useRef(null); // Track threads we should not auto-load yet
  const previousCitationStates = useRef(new Map()); // Track previous citation states

  const syncThreadSelectionFromUrl = useCallback((id) => {
    const normalizedId = id || null;
    setThreadId(normalizedId);
    setActiveThreadId(normalizedId);

    if (normalizedId && skipThreadLoadRef.current === normalizedId) {
      setIsViewingLoadedThread(false);
    } else {
      setIsViewingLoadedThread(!!normalizedId);
    }
  }, []);

  // Sync thread selection with URL params
  useEffect(() => {
    if (urlThreadId !== undefined) {
      syncThreadSelectionFromUrl(urlThreadId || null);
    }
  }, [urlThreadId, syncThreadSelectionFromUrl]);

  // Helper function to scroll to the absolute bottom
  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (messagesListRef.current) {
      // Scroll the container to its maximum scroll height
      messagesListRef.current.scrollTo({
        top: messagesListRef.current.scrollHeight,
        behavior: behavior,
      });
    }
  }, []);

  // Auto-scroll to bottom only when new messages are added to the end
  useEffect(() => {
    // Only scroll if the last message is new or streaming (regular chat flow)
    // Don't scroll for re-ask responses inserted in older positions
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && (lastMessage.streaming || lastMessage.type === "user")) {
      scrollToBottom("smooth");
    }

    // Check for citation state changes and scroll when citations start loading or finish loading
    // Also scroll when feedback UI appears (when citationsLoading goes from true to false)
    let shouldScrollForCitations = false;

    messages.forEach((msg) => {
      if (msg.type === "bot") {
        const msgId = msg.id;
        const prevState = previousCitationStates.current.get(msgId);
        const currentState = {
          citationsLoading: msg.citationsLoading || false,
          hasCitations: msg.citations && msg.citations.length > 0,
          streaming: msg.streaming || false,
        };

        // Check if citations just started loading or just finished loading
        if (prevState) {
          const citationsJustStartedLoading =
            !prevState.citationsLoading && currentState.citationsLoading;
          const citationsJustFinishedLoading =
            prevState.citationsLoading && !currentState.citationsLoading;
          const responseJustFinishedStreaming =
            prevState.streaming && !currentState.streaming;

          if (
            citationsJustStartedLoading ||
            citationsJustFinishedLoading ||
            responseJustFinishedStreaming
          ) {
            shouldScrollForCitations = true;
          }
        } else if (currentState.citationsLoading || currentState.hasCitations) {
          // First time seeing this message with citations - scroll
          shouldScrollForCitations = true;
        }

        // Update the previous state
        previousCitationStates.current.set(msgId, currentState);
      }
    });

    if (shouldScrollForCitations) {
      // Add a delay to ensure DOM has updated (including feedback UI)
      // Use longer delays to ensure feedback buttons have rendered
      setTimeout(() => {
        scrollToBottom("smooth");
      }, 300);

      // Add additional scrolls with longer delays to catch any slow-rendering feedback UI
      setTimeout(() => {
        scrollToBottom("smooth");
      }, 600);
      setTimeout(() => {
        scrollToBottom("smooth");
      }, 900);
    }
  }, [messages, scrollToBottom]);

  // Initialize with a welcome message when component mounts
  useEffect(() => {
    if (selectedBot) {
      // If there's a URL thread ID, don't show welcome message - it will load from thread
      if (urlThreadId) {
        syncThreadSelectionFromUrl(urlThreadId);
        return;
      }

      const welcomeMessage = selectedBot.initialMessage;

      if (welcomeMessage) {
        setMessages([
          {
            id: Date.now(),
            type: "bot",
            content: welcomeMessage,
            timestamp: new Date(),
            isWelcome: true,
          },
        ]);
      }
      setThreadId(null);
      setActiveThreadId(null);
      setIsViewingLoadedThread(false); // Reset when switching bots
    }
  }, [selectedBot, urlThreadId, syncThreadSelectionFromUrl]);

  // Auto-focus the message input when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      messageInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = messageInputRef.current;
    if (textarea) {
      textarea.style.height = "44px";
      // If content requires more height than 44px, expand to scrollHeight
      const newHeight = Math.max(44, textarea.scrollHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputMessage]);

  // Fetch threads from server - reusable function
  const fetchThreads = useCallback(async () => {
    if (!selectedBot || isEmbedded || !token) return;

    try {
      const response = await fetch(
        API_ENDPOINTS.userChatThreads(selectedBot.id),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setThreads((prevThreads) => {
          const serverThreads = data.threads || [];
          if (
            activeThreadId &&
            !serverThreads.some(
              (thread) => String(thread.threadId) === String(activeThreadId),
            )
          ) {
            console.log(
              "Preserving optimistic threads; server missing active thread",
            );
            return prevThreads;
          }
          return serverThreads;
        });
      } else {
        console.error("Failed to fetch threads");
      }
    } catch (error) {
      console.error("Error fetching threads:", error);
    }
  }, [selectedBot, isEmbedded, token, activeThreadId]);

  // Fetch threads on mount and when bot changes
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Load thread messages when threadId changes
  useEffect(() => {
    if (!threadId || !token || isEmbedded) {
      return;
    }

    // Don't load messages if this is a newly created thread (temporary or just created)
    // Only load when user clicks on an existing thread from the sidebar
    if (threadId?.toString().startsWith("temp-")) {
      console.log("Skipping load for temporary thread:", threadId);
      return;
    }

    if (
      skipThreadLoadRef.current &&
      String(skipThreadLoadRef.current) === String(threadId)
    ) {
      console.log("Skipping load for pending thread:", threadId);
      return;
    }

    // Don't try to load if isViewingLoadedThread is explicitly false (new thread)
    if (isViewingLoadedThread === false) {
      console.log(
        "Skipping load for new thread, isViewingLoadedThread is false",
      );
      return;
    }

    const loadThreadMessages = async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINTS.chat}/messages/${threadId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          if (
            skipThreadLoadRef.current &&
            String(skipThreadLoadRef.current) === String(threadId)
          ) {
            skipThreadLoadRef.current = null;
          }
          setIsViewingLoadedThread(true); // Mark that we're viewing a loaded thread
          // Ensure threadId is set when viewing a loaded thread
          if (
            threadId &&
            String(threadId) !== String(data.threadId || threadId)
          ) {
            console.warn("Thread ID mismatch between URL and server response");
          }
          // Transform server messages to component format
          const transformedMessages = data.messages.map((msg) => {
            const messageObj = {
              id: msg.id,
              type: msg.role === "user" ? "user" : "bot",
              content: msg.content,
              timestamp: new Date(msg.createdAt),
              // Transform rating from boolean to "up"/"down" format
              rating:
                msg.rating === true
                  ? "up"
                  : msg.rating === false
                    ? "down"
                    : null,
              feedback: msg.feedback,
              provider: msg.provider,
              model: msg.model,
              // Preserve parentMessageId for reask grouping
              parentMessageId: msg.parentMessageId || null,
            };

            // Add questionGroupId for grouping (use a simple approach)
            if (msg.role === "user") {
              messageObj.questionGroupId = `group_${msg.id}`;
            } else if (msg.role === "assistant") {
              // If this is a reask (has parentMessageId pointing to another assistant message),
              // use the parent's questionGroupId, otherwise find the previous user message
              if (msg.parentMessageId) {
                const parentMsg = data.messages.find(
                  (m) => m.id === msg.parentMessageId,
                );
                if (parentMsg && parentMsg.role === "assistant") {
                  // This is a reask - find the parent's questionGroupId
                  const parentUserMsgIndex = data.messages.findIndex(
                    (m) => m.id === msg.parentMessageId,
                  );
                  if (parentUserMsgIndex > 0) {
                    const prevUserMsg = data.messages
                      .slice(0, parentUserMsgIndex)
                      .reverse()
                      .find((m) => m.role === "user");
                    if (prevUserMsg) {
                      messageObj.questionGroupId = `group_${prevUserMsg.id}`;
                    }
                  }
                } else {
                  // Parent is a user message, use it directly
                  messageObj.questionGroupId = `group_${msg.parentMessageId}`;
                }
              } else {
                // Find the previous user message to get questionGroupId
                const userMsgIndex = data.messages.findIndex(
                  (m) => m.id === msg.id,
                );
                if (userMsgIndex > 0) {
                  const prevUserMsg = data.messages
                    .slice(0, userMsgIndex)
                    .reverse()
                    .find((m) => m.role === "user");
                  if (prevUserMsg) {
                    messageObj.questionGroupId = `group_${prevUserMsg.id}`;
                  }
                }
              }
            }

            return messageObj;
          });

          // Don't prepend welcome message when loading thread - thread already has its history
          setMessages(transformedMessages);

          // Scroll to bottom immediately without animation and focus input
          requestAnimationFrame(() => {
            scrollToBottom("instant");
            setTimeout(() => {
              messageInputRef.current?.focus();
            }, 100);
          });
        } else {
          console.error("Failed to load thread messages");
          setIsViewingLoadedThread(false);
        }
      } catch (error) {
        console.error("Error loading thread messages:", error);
        setIsViewingLoadedThread(false);
      }
    };

    loadThreadMessages();
  }, [
    threadId,
    token,
    isEmbedded,
    selectedBot,
    isViewingLoadedThread,
    scrollToBottom,
  ]);

  // Handle thread selection
  const handleThreadSelect = (selectedThreadId) => {
    if (selectedThreadId === threadId) return; // Already selected

    // Mark that we want to load this thread from the server
    setIsViewingLoadedThread(true);
    setActiveThreadId(selectedThreadId);
    setThreadId(selectedThreadId);

    // Update URL to include thread ID
    if (!isEmbedded && selectedBot) {
      navigate(`/bots/${selectedBot.id}/thread/${selectedThreadId}`, {
        replace: true,
      });
    }
    // Messages will be loaded by the useEffect above
  };

  // Handle new chat
  const handleNewChat = () => {
    if (!threadId) return; // Already on new chat
    const welcomeMessage = selectedBot?.initialMessage;
    if (welcomeMessage) {
      setMessages([
        {
          id: Date.now(),
          type: "bot",
          content: welcomeMessage,
          timestamp: new Date(),
          isWelcome: true,
        },
      ]);
    } else {
      setMessages([]);
    }
    setThreadId(null);
    setActiveThreadId(null);
    setIsViewingLoadedThread(false);
    setActiveTabs({});
    // Update URL to remove thread ID
    if (!isEmbedded && selectedBot) {
      navigate(`/bots/${selectedBot.id}`, { replace: true });
    }
    // Focus input after clearing
    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 100);
  };

  const handleTitleSave = async (threadId) => {
    const trimmedTitle = editingTitle.trim();
    setEditingThreadId(null);
    setEditingTitle("");

    // Don't save if title is empty or unchanged
    const thread = threads.find((t) => t.threadId === threadId);
    if (!trimmedTitle || trimmedTitle === thread?.title) {
      return;
    }

    // Optimistically update UI
    setThreads((prev) =>
      prev.map((t) =>
        t.threadId === threadId ? { ...t, title: trimmedTitle } : t
      )
    );

    try {
      const response = await fetch(API_ENDPOINTS.updateThreadTitle(threadId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title: trimmedTitle }),
      });

      if (!response.ok) {
        throw new Error("Failed to update title");
      }
    } catch (error) {
      console.error("Error updating thread title:", error);
      // Revert on error
      setThreads((prev) =>
        prev.map((t) =>
          t.threadId === threadId ? { ...t, title: thread?.title } : t
        )
      );
    }
  };

  const handleRating = async (messageId, rating) => {
    // Convert to string for consistent comparison since loaded messages have string IDs
    const messageIdStr = String(messageId);
    const currentMessage = messages.find(
      (msg) => String(msg.id) === messageIdStr,
    );
    const newRating = currentMessage?.rating === rating ? null : rating;

    // Optimistically update the UI
    setMessages((prev) =>
      prev.map((msg) =>
        String(msg.id) === messageIdStr ? { ...msg, rating: newRating } : msg,
      ),
    );

    // Make API call
    try {
      const ratingValue =
        newRating === "up" ? true : newRating === "down" ? false : null;

      const response = await fetch(API_ENDPOINTS.chatRate(messageId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: ratingValue }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error submitting rating:", error);

      // Revert the optimistic update on error
      setMessages((prev) =>
        prev.map((msg) =>
          String(msg.id) === messageIdStr
            ? { ...msg, rating: currentMessage?.rating }
            : msg,
        ),
      );

      // Could show a toast notification here
      alert("Failed to submit rating. Please try again.");
    }
  };

  const handleFeedbackToggle = (messageId) => {
    if (showFeedbackFor === messageId) {
      setShowFeedbackFor(null);
      setFeedbackText("");
    } else {
      setShowFeedbackFor(messageId);
      const message = messages.find((msg) => msg.id === messageId);
      setFeedbackText(message?.feedback || "");

      // Scroll to show the feedback input after it renders
      setTimeout(() => {
        scrollToBottom("smooth");
      }, 100);
    }
  };

  const handleFeedbackEdit = (messageId) => {
    setShowFeedbackFor(messageId);
    const message = messages.find((msg) => msg.id === messageId);
    setFeedbackText(message?.feedback || "");

    // Scroll to show the feedback input after it renders
    setTimeout(() => {
      scrollToBottom("smooth");
    }, 100);
  };

  const handleFeedbackSave = async (messageId) => {
    const trimmedFeedback = feedbackText.trim();
    const feedbackValue = trimmedFeedback || null;

    // Optimistically update the UI
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, feedback: trimmedFeedback } : msg,
      ),
    );

    setShowFeedbackFor(null);
    setFeedbackText("");

    // Make API call
    try {
      const response = await fetch(API_ENDPOINTS.chatFeedback(messageId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ feedback: feedbackValue }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);

      // Could show a toast notification here
      alert("Failed to submit feedback. Please try again.");

      // Note: We don't revert the UI change here since the user might want to try again
      // and we don't want to lose their typed feedback
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackFor(null);
    setFeedbackText("");
  };

  const handleFeedbackKeyPress = (e, messageId) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleFeedbackSave(messageId);
    }
  };

  const handleReask = async (providerModelOption, botMessageId, reaskTabId) => {
    const hasStreamingMessage = messages.some((msg) => msg.streaming);
    if (loading || hasStreamingMessage) return;

    // Find the original bot message to get its questionGroupId and threadId
    const originalBotMessage = messages.find((msg) => msg.id === botMessageId);
    if (!originalBotMessage || !originalBotMessage.questionGroupId) {
      console.error("Could not find original bot message or questionGroupId");
      return;
    }

    const questionGroupId = originalBotMessage.questionGroupId; // Capture for use in closures

    // Check if any message in this question group has disableReAsk flag set
    const groupMessages = messages.filter(
      (msg) => msg.questionGroupId === questionGroupId,
    );
    const hasDisableReAsk = groupMessages.some(
      (msg) => msg.disableReAsk === true,
    );
    if (hasDisableReAsk) {
      console.log("Re-ask disabled for this message group");
      return;
    }

    const originalThreadId = originalBotMessage.threadId; // Use the original message's thread ID

    setLoading(true);

    // Create initial bot message for streaming with same questionGroupId
    // Use the reaskTabId if provided, otherwise generate a new one
    const newBotMessageId = reaskTabId || Date.now();
    const initialBotMessage = {
      id: newBotMessageId,
      type: "bot",
      content: "",
      timestamp: new Date(),
      streaming: true,
      reaskFrom: selectedBot.name,
      reaskTo: providerModelOption.name,
      questionGroupId: questionGroupId,
      isReaskResponse: true, // Mark as re-ask response
      citations: [], // Clear previous citations
      citationsLoading: false, // Start with citations not loading
    };

    // Insert the re-ask response right after the last message in its question group
    setMessages((prev) => {
      const newMessages = [...prev];

      // Find the index of the last message in this question group
      let insertIndex = newMessages.length; // Default to end if not found
      for (let i = newMessages.length - 1; i >= 0; i--) {
        if (newMessages[i].questionGroupId === questionGroupId) {
          insertIndex = i + 1;
          break;
        }
      }

      // Insert the new message at the correct position
      newMessages.splice(insertIndex, 0, initialBotMessage);
      return newMessages;
    });

    // Set this as the active tab for this question group
    setActiveTabs((prev) => ({
      ...prev,
      [questionGroupId]: newBotMessageId,
    }));

    try {
      const response = await fetch(API_ENDPOINTS.chatReask, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          originalMessageId: botMessageId, // Use bot message ID instead of user message ID
          newBotId: selectedBot.id, // Keep the same bot
          provider: providerModelOption.provider,
          model: providerModelOption.model,
          username: user?.username || "None",
          stream: true,
          createNewThread: false,
          ...(originalThreadId && { threadId: originalThreadId }), // Include original thread ID if available
        }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = "";
      let currentThreadId = originalThreadId; // Use original message's thread ID
      let serverMessageId = null;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const jsonStr = line.slice(6); // Remove 'data: ' prefix
              const data = JSON.parse(jsonStr);

              // Update thread ID if provided (allow server to provide new thread for re-ask)
              if (data.threadId) {
                currentThreadId = data.threadId;
                // Don't update global threadId for re-ask operations to avoid affecting other messages
              }

              // Track server messageId if provided
              if (data.messageId) {
                serverMessageId = data.messageId;
              }

              // Accumulate content from response field
              if (data.response) {
                accumulatedContent += data.response;

                let displayContent = accumulatedContent;
                let partialCitations = null;
                let partialConfidence = null;
                let citationsLoading = false;
                let responseComplete = false;

                if (data.structured) {
                  try {
                    const strippedContent = accumulatedContent.replace(/^```(?:json)?\s*\n?/, "").replace(/\n?```\s*$/, "");
                    const parsed = JSON.parse(strippedContent);
                    if (parsed.response) {
                      displayContent = parsed.response;
                    }
                    if (
                      parsed.citations &&
                      Array.isArray(parsed.citations) &&
                      parsed.citations.length > 0
                    ) {
                      partialCitations = parsed.citations;
                    }
                    if (typeof parsed.confidence === "number") {
                      partialConfidence = parsed.confidence;
                    }
                  } catch (parseError) {
                    displayContent =
                      extractResponseFromPartialJson(strippedContent);
                  }

                  // Check for citations in both escaped and unescaped formats (with and without spaces)
                  const hasCitationsPattern =
                    accumulatedContent.includes('\\"citations\\":[') ||
                    accumulatedContent.includes('\\"citations\\": [') ||
                    accumulatedContent.includes('"citations":[') ||
                    accumulatedContent.includes('"citations": [') ||
                    accumulatedContent.includes('\\\\"citations\\\\"');

                  if (hasCitationsPattern) {
                    responseComplete = true;
                    if (!partialCitations) {
                      citationsLoading = true;
                    }
                  }
                }

                const contentToUpdate = displayContent;
                const threadIdToUpdate = currentThreadId;
                const messageIdToUpdate = serverMessageId;
                const citationsToUpdate = partialCitations;
                const confidenceToUpdate = partialConfidence;
                const citationsLoadingToUpdate = citationsLoading;
                const responseCompleteToUpdate = responseComplete;

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === newBotMessageId || msg.id === messageIdToUpdate
                      ? {
                          ...msg,
                          content: contentToUpdate,
                          threadId: threadIdToUpdate,
                          isStructured: data.structured || msg.isStructured,
                          citations: citationsToUpdate || msg.citations,
                          confidence: confidenceToUpdate ?? msg.confidence,
                          responseComplete:
                            responseCompleteToUpdate || msg.responseComplete,
                          citationsLoading: citationsLoadingToUpdate,
                          ...(messageIdToUpdate && { id: messageIdToUpdate }),
                        }
                      : msg,
                  ),
                );
              }

              // Capture model/provider information if provided
              if (data.model || data.provider) {
                const modelToUpdate = data.model;
                const providerToUpdate = data.provider;
                const messageIdToUpdate = serverMessageId;

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === newBotMessageId || msg.id === messageIdToUpdate
                      ? {
                          ...msg,
                          model: modelToUpdate,
                          provider: providerToUpdate,
                          // Preserve citationsLoading to prevent it from becoming undefined
                          citationsLoading: msg.citationsLoading ?? false,
                          ...(messageIdToUpdate && { id: messageIdToUpdate }),
                        }
                      : msg,
                  ),
                );
              }

              // Check if streaming is complete
              if (data.done) {
                const messageIdToUpdate = serverMessageId;
                const questionGroupIdToUpdate = questionGroupId;
                const structuredData = data.structuredData;

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === newBotMessageId || msg.id === messageIdToUpdate
                      ? {
                          ...msg,
                          streaming: false,
                          citationsLoading: false,
                          responseComplete: true, // Final state is always complete
                          // Store server-provided flags
                          ...(data.disableReAsk !== undefined && {
                            disableReAsk: data.disableReAsk,
                          }),
                          ...(data.piiDetected !== undefined && {
                            piiDetected: data.piiDetected,
                          }),
                          ...(structuredData && {
                            content: structuredData.response || msg.content,
                            citations:
                              structuredData.citations || msg.citations,
                            confidence:
                              structuredData.confidence ?? msg.confidence,
                          }),
                          ...(messageIdToUpdate && { id: messageIdToUpdate }),
                        }
                      : msg,
                  ),
                );

                // Auto-select the completed response tab
                setActiveTabs((prev) => ({
                  ...prev,
                  [questionGroupIdToUpdate]:
                    messageIdToUpdate || newBotMessageId,
                }));

                // Scroll to bottom when streaming completes to show feedback buttons
                // Use multiple delays to ensure feedback buttons have rendered
                setTimeout(() => {
                  scrollToBottom("smooth");
                }, 300);
                setTimeout(() => {
                  scrollToBottom("smooth");
                }, 600);
                setTimeout(() => {
                  scrollToBottom("smooth");
                }, 800);

                // Refresh threads after a delay to pick up auto-generated titles
                setTimeout(() => {
                  fetchThreads();
                }, 2000);

                return;
              }
            } catch (parseError) {
              console.error("Error parsing streaming data:", parseError);
            }
          }
        }
      }

      // Ensure streaming flag is removed when done
      // Capture current value to avoid unsafe reference in closure
      const finalMessageId = serverMessageId;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === newBotMessageId || msg.id === finalMessageId
            ? {
                ...msg,
                streaming: false,
                // Preserve citationsLoading to prevent it from becoming undefined
                citationsLoading: msg.citationsLoading ?? false,
              }
            : msg,
        ),
      );
    } catch (error) {
      console.error("Error re-asking message:", error);

      // Remove the placeholder bot message and add error message
      setMessages((prev) => {
        const filteredMessages = prev.filter(
          (msg) => msg.id !== newBotMessageId,
        );
        return [
          ...filteredMessages,
          {
            id: Date.now() + 1,
            type: "error",
            content: `Sorry, I encountered an error while re-asking with ${providerModelOption.name}. Please try again.`,
            timestamp: new Date(),
          },
        ];
      });
    } finally {
      setLoading(false);
    }
  };

  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB
  const MAX_TEXT_FILE_SIZE = 10 * 1024 * 1024; // 10MB for text/PDF/Excel
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB for images (Anthropic limit)

  const getFileError = (file) => {
    const isImage = file.type.startsWith("image/");
    const maxSize = isImage ? MAX_IMAGE_SIZE : MAX_TEXT_FILE_SIZE;
    const maxSizeMB = maxSize / (1024 * 1024);

    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return `${fileSizeMB}MB exceeds ${maxSizeMB}MB limit`;
    }
    return null;
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;

    // Combine with existing files
    const combinedFiles = [...selectedFiles, ...newFiles];

    // Check file count limit
    if (combinedFiles.length > MAX_FILES) {
      alert(`Maximum ${MAX_FILES} files allowed. You have ${combinedFiles.length} files selected.`);
      e.target.value = ""; // Reset file input
      return;
    }

    // Check aggregate size limit
    const totalSize = combinedFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
      alert(`Combined file size (${totalSizeMB}MB) exceeds the 25MB limit.`);
      e.target.value = ""; // Reset file input
      return;
    }

    setSelectedFiles(combinedFiles);
    e.target.value = ""; // Reset file input to allow re-selecting same files
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    const hasStreamingMessage = messages.some((msg) => msg.streaming);
    if (!inputMessage.trim() || loading || hasStreamingMessage) return;

    // Capture values before clearing state
    const messageToSend = inputMessage.trim();
    const filesToSend = [...selectedFiles];

    const questionGroupId = `group_${Date.now()}`;
    const userMessageId = Date.now();

    // If files are being sent, add acknowledgment to the message content
    const displayContent = filesToSend.length > 0
      ? `📄 ${filesToSend.map(f => f.name).join(", ")}\n\n${messageToSend}`
      : messageToSend;

    const userMessage = {
      id: userMessageId,
      type: "user",
      content: displayContent,
      timestamp: new Date(),
      questionGroupId,
    };

    // Add user message to chat
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setInputMessage(""); // Clear input immediately after sending
    setSelectedFiles([]); // Clear file selection immediately

    // Create initial bot message for streaming
    const botMessageId = Date.now() + 1;
    const initialBotMessage = {
      id: botMessageId,
      type: "bot",
      content: "",
      timestamp: new Date(),
      streaming: true,
      questionGroupId,
      isOriginalResponse: true, // Mark as original response
      citations: [], // Initialize empty citations
      citationsLoading: false, // Start with citations not loading
    };

    setMessages((prev) => [...prev, initialBotMessage]);

    // Set this as the active tab for this question group
    setActiveTabs((prev) => ({ ...prev, [questionGroupId]: botMessageId }));

    let tempThreadId = null;
    // If starting a brand new conversation, add a temporary thread entry so it appears immediately
    if (!threadId && !isEmbedded && selectedBot) {
      tempThreadId = `temp-${Date.now()}`;
      console.log("Creating temporary thread for new chat:", tempThreadId);

      setThreads((prevThreads) => [
        {
          threadId: tempThreadId,
          botId: selectedBot.id,
          botName: selectedBot.name,
          lastMessageAt: new Date().toISOString(),
          messageCount: 0,
          userMessageCount: 0,
          assistantMessageCount: 0,
          isTemporary: true,
        },
        ...prevThreads,
      ]);

      setActiveThreadId(tempThreadId);
      setIsViewingLoadedThread(false);
    }

    try {
      let body;
      let headers = {
        Authorization: `Bearer ${token}`,
      };

      if (filesToSend.length > 0) {
        const formData = new FormData();
        formData.append("botId", selectedBot.id);
        formData.append("stream", "true");
        formData.append("content", messageToSend);
        formData.append("username", user?.username || "None");
        if (threadId) {
          formData.append("threadId", threadId);
        }
        // Append each file with the same field name for multer.array()
        filesToSend.forEach((file) => {
          formData.append("files", file);
        });
        body = formData;
        // Content-Type header will be set automatically by browser with boundary
      } else {
        headers["Content-Type"] = "application/json";
        const requestBody = {
          botId: selectedBot.id,
          stream: true,
          content: messageToSend,
          username: user?.username || "None",
        };

        // Include threadId if we have one (either from loaded thread or newly created)
        // This ensures conversation history is retrieved on the server
        if (threadId) {
          requestBody.threadId = threadId;
        } else if (isViewingLoadedThread) {
          // If we're viewing a loaded thread but threadId is missing, log a warning
          console.warn("Viewing loaded thread but threadId is not set");
        }
        body = JSON.stringify(requestBody);
      }

      const response = await fetch(API_ENDPOINTS.chat, {
        method: "POST",
        headers,
        body,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = "";
      let currentThreadId = threadId;
      let serverMessageId = null; // Track server-provided messageId for bot message
      let serverUserMessageId = null; // Track server-provided messageId for user message

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const jsonStr = line.slice(6); // Remove 'data: ' prefix
              const data = JSON.parse(jsonStr);

              // Update thread ID if provided
              if (data.threadId && !currentThreadId) {
                currentThreadId = data.threadId;
                const newThreadId = data.threadId;
                console.log(
                  "New thread created:",
                  newThreadId,
                  "Type:",
                  typeof newThreadId,
                );

                // First, add thread to list, THEN set it as selected
                // This ensures React processes them in order
                if (!isEmbedded && selectedBot) {
                  console.log("Adding new thread to sidebar:", newThreadId);

                  // Add thread to list first
                  setThreads((prevThreads) => {
                    const threadsWithoutTemp = tempThreadId
                      ? prevThreads.filter(
                          (t) => String(t.threadId) !== String(tempThreadId),
                        )
                      : prevThreads;

                    // Check if thread already exists
                    const existingThread = threadsWithoutTemp.find(
                      (t) => String(t.threadId) === String(newThreadId),
                    );
                    if (existingThread) {
                      console.log("Thread already exists in sidebar");
                      return threadsWithoutTemp;
                    }

                    console.log("Adding new thread optimistically to sidebar");
                    // Add new thread at the top
                    const newThreads = [
                      {
                        threadId: newThreadId,
                        botId: selectedBot.id,
                        botName: selectedBot.name,
                        lastMessageAt: new Date().toISOString(),
                        messageCount: 2, // User message + bot message
                        userMessageCount: 1,
                        assistantMessageCount: 1,
                      },
                      ...threadsWithoutTemp,
                    ];
                    console.log(
                      "New threads array:",
                      newThreads.length,
                      "threads, new thread ID:",
                      newThreadId,
                    );
                    return newThreads;
                  });

                  // Refresh thread list after a longer delay to ensure server has fully processed
                  if (token) {
                    setTimeout(() => {
                      console.log("Refreshing threads from server...");
                      fetch(API_ENDPOINTS.userChatThreads(selectedBot.id), {
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                      })
                        .then((res) => res.json())
                        .then((data) => {
                          console.log(
                            "Server returned threads:",
                            data.threads?.length,
                          );
                          setThreads((prevThreads) => {
                            const serverThreads = data.threads || [];
                            // If server doesn't have the new thread yet, keep our optimistic version
                            const hasNewThread = serverThreads.some(
                              (t) => String(t.threadId) === String(newThreadId),
                            );
                            if (!hasNewThread) {
                              console.log(
                                "Server doesn't have new thread yet, keeping optimistic version",
                              );
                              return prevThreads; // Keep the optimistic update
                            }
                            console.log(
                              "Server has new thread, using server data",
                            );
                            if (
                              skipThreadLoadRef.current &&
                              String(skipThreadLoadRef.current) ===
                                String(newThreadId)
                            ) {
                              skipThreadLoadRef.current = null;
                            }
                            return serverThreads;
                          });
                        })
                        .catch((err) =>
                          console.error("Error refreshing threads:", err),
                        );
                    }, 1000); // Wait 1 second to give server time to save the thread
                  }
                }

                // Then set as selected and update URL
                console.log("Setting threadId state to:", newThreadId);
                skipThreadLoadRef.current = newThreadId;
                setActiveThreadId(newThreadId);
                setThreadId(newThreadId);
                setIsViewingLoadedThread(false); // New message in current thread, not viewing loaded thread

                // Update URL with new thread ID
                if (!isEmbedded && selectedBot) {
                  navigate(`/bots/${selectedBot.id}/thread/${newThreadId}`, {
                    replace: true,
                  });
                }
              }

              // Track server messageId for bot message if provided
              if (data.messageId) {
                serverMessageId = data.messageId;
              }

              // Track server messageId for user message if provided
              if (data.userMessageId) {
                serverUserMessageId = data.userMessageId;

                // Capture current value to avoid unsafe references in closure
                const userMessageIdToUpdate = serverUserMessageId;

                // Update the user message with server-provided ID
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === userMessageId
                      ? { ...msg, id: userMessageIdToUpdate }
                      : msg,
                  ),
                );
              }

              // Accumulate content from response field
              if (data.response) {
                accumulatedContent += data.response;

                let displayContent = accumulatedContent;
                let partialCitations = null;
                let partialConfidence = null;
                let citationsLoading = false;
                let responseComplete = false;

                if (data.structured) {
                  try {
                    const strippedContent = accumulatedContent.replace(/^```(?:json)?\s*\n?/, "").replace(/\n?```\s*$/, "");
                    const parsed = JSON.parse(strippedContent);
                    if (parsed.response) {
                      displayContent = parsed.response;
                    }
                    if (
                      parsed.citations &&
                      Array.isArray(parsed.citations) &&
                      parsed.citations.length > 0
                    ) {
                      partialCitations = parsed.citations;
                    }
                    if (typeof parsed.confidence === "number") {
                      partialConfidence = parsed.confidence;
                    }
                  } catch (parseError) {
                    displayContent =
                      extractResponseFromPartialJson(strippedContent);
                  }

                  // Check for citations in both escaped and unescaped formats (with and without spaces)
                  if (
                    accumulatedContent.includes('\\"citations\\":[') ||
                    accumulatedContent.includes('\\"citations\\": [') ||
                    accumulatedContent.includes('"citations":[') ||
                    accumulatedContent.includes('"citations": [') ||
                    accumulatedContent.includes('\\\\"citations\\\\"')
                  ) {
                    responseComplete = true;
                    if (!partialCitations) {
                      citationsLoading = true;
                    }
                  }
                }

                const contentToUpdate = displayContent;
                const threadIdToUpdate = currentThreadId;
                const messageIdToUpdate = serverMessageId;
                const citationsToUpdate = partialCitations;
                const confidenceToUpdate = partialConfidence;
                const citationsLoadingToUpdate = citationsLoading;
                const responseCompleteToUpdate = responseComplete;

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId || msg.id === messageIdToUpdate
                      ? {
                          ...msg,
                          content: contentToUpdate,
                          threadId: threadIdToUpdate,
                          isStructured: data.structured || msg.isStructured,
                          citations: citationsToUpdate || msg.citations,
                          confidence: confidenceToUpdate ?? msg.confidence,
                          responseComplete:
                            responseCompleteToUpdate || msg.responseComplete,
                          citationsLoading: citationsLoadingToUpdate,
                          ...(messageIdToUpdate && { id: messageIdToUpdate }),
                        }
                      : msg,
                  ),
                );
              }

              // Capture model/provider information if provided
              if (data.model || data.provider) {
                const modelToUpdate = data.model;
                const providerToUpdate = data.provider;
                const messageIdToUpdate = serverMessageId;

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId || msg.id === messageIdToUpdate
                      ? {
                          ...msg,
                          model: modelToUpdate,
                          provider: providerToUpdate,
                          // Preserve citationsLoading to prevent it from becoming undefined
                          citationsLoading: msg.citationsLoading ?? false,
                          ...(messageIdToUpdate && { id: messageIdToUpdate }),
                        }
                      : msg,
                  ),
                );
              }

              // Check if streaming is complete
              if (data.done) {
                const messageIdToUpdate = serverMessageId;
                const questionGroupIdToUpdate = questionGroupId;
                const structuredData = data.structuredData;

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId || msg.id === messageIdToUpdate
                      ? {
                          ...msg,
                          streaming: false,
                          citationsLoading: false,
                          responseComplete: true, // Final state is always complete
                          // Store server-provided flags
                          ...(data.disableReAsk !== undefined && {
                            disableReAsk: data.disableReAsk,
                          }),
                          ...(data.piiDetected !== undefined && {
                            piiDetected: data.piiDetected,
                          }),
                          ...(structuredData && {
                            content: structuredData.response || msg.content,
                            citations:
                              structuredData.citations || msg.citations,
                            confidence:
                              structuredData.confidence ?? msg.confidence,
                          }),
                          ...(messageIdToUpdate && { id: messageIdToUpdate }),
                        }
                      : msg,
                  ),
                );

                // Auto-select the completed response tab
                setActiveTabs((prev) => ({
                  ...prev,
                  [questionGroupIdToUpdate]: messageIdToUpdate || botMessageId,
                }));

                // Scroll to bottom when streaming completes to show feedback buttons
                // Use multiple delays to ensure feedback buttons have rendered
                setTimeout(() => {
                  scrollToBottom("smooth");
                }, 300);
                setTimeout(() => {
                  scrollToBottom("smooth");
                }, 600);
                setTimeout(() => {
                  scrollToBottom("smooth");
                  messageInputRef.current?.focus();
                }, 800);

                // Refresh threads after a delay to pick up auto-generated titles
                setTimeout(() => {
                  fetchThreads();
                }, 2000);
                return;
              }
            } catch (parseError) {
              console.error("Error parsing streaming data:", parseError);
            }
          }
        }
      }

      // Ensure streaming flag is removed when done
      // Capture current value to avoid unsafe reference in closure
      const finalMessageId = serverMessageId;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessageId || msg.id === finalMessageId
            ? {
                ...msg,
                streaming: false,
                // Preserve citationsLoading to prevent it from becoming undefined
                citationsLoading: msg.citationsLoading ?? false,
              }
            : msg,
        ),
      );

      // Focus the input field when streaming is done
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error("Error sending message:", error);

      // Remove the placeholder bot message and add error message
      setMessages((prev) => {
        const filteredMessages = prev.filter((msg) => msg.id !== botMessageId);
        return [
          ...filteredMessages,
          {
            id: Date.now() + 2,
            type: "error",
            content:
              "Sorry, I encountered an error while processing your message. Please try again.",
            timestamp: new Date(),
          },
        ];
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const hasStreamingMessage = messages.some((msg) => msg.streaming);
      if (!loading && !hasStreamingMessage) {
        sendMessage();
      }
    }
  };

  // Group messages by question groups
  const groupMessages = useCallback(() => {
    // When viewing a loaded thread, group reask responses similar to AdminThread.js
    if (isViewingLoadedThread) {
      const groups = [];
      const processedMessageIds = new Set();

      // Sort messages by timestamp to maintain chronological order
      const sortedMessages = [...messages].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      );

      // First pass: Find all assistant messages that reference other assistant messages (reasks)
      // and group them by their parentMessageId
      const assistantResponseGroups = new Map();

      sortedMessages.forEach((message) => {
        if (message.type === "bot" && message.parentMessageId) {
          const parentMessage = sortedMessages.find(
            (m) => m.id === message.parentMessageId,
          );

          if (parentMessage && parentMessage.type === "bot") {
            // This is a reask response to another assistant message
            if (!assistantResponseGroups.has(message.parentMessageId)) {
              assistantResponseGroups.set(message.parentMessageId, []);
            }
            assistantResponseGroups.get(message.parentMessageId).push(message);
            processedMessageIds.add(message.id);
          }
        }
      });

      // Create groups for assistant message responses (reasks)
      assistantResponseGroups.forEach((responses, parentId) => {
        const parentMessage = sortedMessages.find((m) => m.id === parentId);
        if (parentMessage) {
          const allResponsesChronological = [parentMessage, ...responses];

          // The first response chronologically should be the original
          const originalResponseId = allResponsesChronological[0]?.id;

          // Sort responses (original first, then by model priority)
          const allResponses = allResponsesChronological.sort((a, b) => {
            if (a.id === originalResponseId) return -1;
            if (b.id === originalResponseId) return 1;
            return 0;
          });

          // Mark parent as processed too
          processedMessageIds.add(parentId);

          // Find the user message that this group belongs to
          const userMessage = sortedMessages.find(
            (m) =>
              m.type === "user" &&
              m.questionGroupId === parentMessage.questionGroupId,
          );

          groups.push({
            id: `group_${parentId}_responses`,
            userMessage: userMessage || null,
            botResponses: allResponses,
            parentResponse: parentMessage,
          });
        }
      });

      // Second pass: Handle remaining messages
      sortedMessages.forEach((message) => {
        if (processedMessageIds.has(message.id)) return;

        if (message.type === "user" && message.questionGroupId) {
          // Find all assistant responses to this user message
          const responsesChronological = sortedMessages.filter(
            (m) =>
              m.type === "bot" &&
              m.questionGroupId === message.questionGroupId &&
              !processedMessageIds.has(m.id),
          );

          // The first response chronologically should be the original
          const originalResponseId = responsesChronological[0]?.id;

          // Sort responses (original first, then by model priority)
          const responses = responsesChronological.sort((a, b) => {
            if (a.id === originalResponseId) return -1;
            if (b.id === originalResponseId) return 1;
            return 0;
          });

          // Mark responses as processed
          responses.forEach((r) => processedMessageIds.add(r.id));
          processedMessageIds.add(message.id);

          groups.push({
            id: message.questionGroupId,
            userMessage: message,
            botResponses: responses,
          });
        } else if (
          message.type === "bot" &&
          !processedMessageIds.has(message.id)
        ) {
          // Orphaned bot message (no parent or parent not found)
          processedMessageIds.add(message.id);

          groups.push({
            id: `single_${message.id}`,
            userMessage: null,
            botResponses: [message],
          });
        }
      });

      // Sort groups by the earliest message timestamp to maintain chronological order
      groups.sort((a, b) => {
        const getEarliestTimestamp = (group) => {
          const allMessages = [
            ...(group.userMessage ? [group.userMessage] : []),
            ...group.botResponses,
          ];
          return Math.min(
            ...allMessages.map((m) => new Date(m.timestamp).getTime()),
          );
        };

        return getEarliestTimestamp(a) - getEarliestTimestamp(b);
      });

      return groups;
    }

    // Original grouping logic for new messages
    const groups = [];
    const groupsMap = new Map(); // Track groups by ID for efficient lookup

    messages.forEach((message) => {
      if (message.type === "user" && message.questionGroupId) {
        // Start a new group with the user message
        const newGroup = {
          id: message.questionGroupId,
          userMessage: message,
          botResponses: [],
        };
        groups.push(newGroup);
        groupsMap.set(message.questionGroupId, newGroup);
      } else if (message.type === "bot" && message.questionGroupId) {
        // Find the group this bot message belongs to
        const targetGroup = groupsMap.get(message.questionGroupId);
        if (targetGroup) {
          // Add bot response to the correct group
          targetGroup.botResponses.push(message);
        } else {
          // This shouldn't happen, but handle orphaned bot messages
          console.warn(
            `Bot message ${message.id} has questionGroupId ${message.questionGroupId} but no matching user message found`,
          );
        }
      } else if (!message.questionGroupId) {
        // Handle legacy messages or welcome messages without grouping
        groups.push({
          id: `single_${message.id}`,
          userMessage: null,
          botResponses: [message],
        });
      }
    });

    return groups;
  }, [messages, isViewingLoadedThread]);

  // Get all possible re-ask options (constant set, never changes)
  const getAllReaskOptions = useCallback(() => {
    // Don't show placeholder reask options when viewing a loaded thread
    // (we'll show tabs for existing reask responses instead)
    if (isViewingLoadedThread) {
      return [];
    }
    // Exclude only the original bot's model to avoid exact duplicates
    return REASK_MODEL_OPTIONS.filter((option) => {
      // Don't show the exact same model as the original bot
      if (selectedBot?.model && option.model === selectedBot.model) {
        return false;
      }
      return true;
    });
  }, [selectedBot, isViewingLoadedThread]);

  // Ensure first response is active when tabs become visible
  useEffect(() => {
    const groups = groupMessages();

    setActiveTabs((prevActiveTabs) => {
      const updates = { ...prevActiveTabs };
      let hasChanges = false;

      groups.forEach((group) => {
        // Check if this group should show tabs and doesn't have an active tab
        if (
          (group.botResponses.length > 1 || getAllReaskOptions().length > 0) &&
          !group.botResponses.some((r) => r.isWelcome) &&
          !updates[group.id] &&
          group.botResponses.length > 0
        ) {
          updates[group.id] = group.botResponses[0].id;
          hasChanges = true;
        }
      });

      return hasChanges ? updates : prevActiveTabs;
    });
  }, [messages, groupMessages, getAllReaskOptions]);

  // Clear preserved dimensions when streaming completes
  useEffect(() => {
    const groups = groupMessages();
    setMessageHeights((prev) => {
      const newHeights = { ...prev };
      let hasChanges = false;

      groups.forEach((group) => {
        // If no responses are streaming for this group, clear the preserved height
        const hasStreamingResponse = group.botResponses.some(
          (r) => r.streaming,
        );
        if (!hasStreamingResponse && newHeights[group.id]) {
          delete newHeights[group.id];
          hasChanges = true;
        }
      });

      return hasChanges ? newHeights : prev;
    });

    setMessageWidths((prev) => {
      const newWidths = { ...prev };
      let hasChanges = false;

      groups.forEach((group) => {
        // If no responses are streaming for this group, clear the preserved width
        const hasStreamingResponse = group.botResponses.some(
          (r) => r.streaming,
        );
        if (!hasStreamingResponse && newWidths[group.id]) {
          delete newWidths[group.id];
          hasChanges = true;
        }
      });

      return hasChanges ? newWidths : prev;
    });
  }, [messages, groupMessages]);

  // Handle tab click - either switch to existing response or trigger re-ask
  const handleTabClick = (
    groupId,
    tabId,
    isReaskOption = false,
    reaskOption = null,
    originalResponseId = null,
    currentMessageElement = null,
  ) => {
    // Capture current message dimensions before switching (for re-ask scenarios)
    if (isReaskOption && currentMessageElement) {
      const currentHeight = currentMessageElement.scrollHeight;
      const currentWidth = currentMessageElement.scrollWidth;
      setMessageHeights((prev) => ({
        ...prev,
        [groupId]: currentHeight,
      }));
      setMessageWidths((prev) => ({
        ...prev,
        [groupId]: currentWidth,
      }));
    }

    if (isReaskOption && reaskOption && originalResponseId) {
      // Trigger re-ask using the tabId as the target message ID
      handleReask(
        reaskOption,
        originalResponseId,
        `reask-${reaskOption.id}-${groupId}`,
      );
    } else {
      // Switch to existing response - clear preserved dimensions
      setMessageHeights((prev) => {
        const newHeights = { ...prev };
        delete newHeights[groupId];
        return newHeights;
      });
      setMessageWidths((prev) => {
        const newWidths = { ...prev };
        delete newWidths[groupId];
        return newWidths;
      });
      handleTabSwitch(groupId, tabId);
    }
  };

  // Handle tab switching
  const handleTabSwitch = (questionGroupId, botMessageId) => {
    setActiveTabs((prev) => ({
      ...prev,
      [questionGroupId]: botMessageId,
    }));
  };

  return (
    <MobileWrapper>
      <MainContent $isEmbedded={isEmbedded}>
        <MessagesContainer $isEmbedded={isEmbedded}>
          {!isEmbedded && (
            <PageHeader
              title={`${selectedBot?.name || "Assistant"}`}
              showBackButton={true}
              onBack={onBack}
            />
          )}

          <ChatContainer $isEmbedded={isEmbedded}>
            <MessagesList ref={messagesListRef} $isEmbedded={isEmbedded}>
              <MessagesWrapper $isEmbedded={isEmbedded}>
                {groupMessages().map((group) => (
                  <div
                    key={group.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    {/* User message */}
                    {group.userMessage && (
                      <Message $type="user">
                        <MessageContent $type="user">
                          <MessageText>
                            {processMessageContent(group.userMessage.content)}
                          </MessageText>
                        </MessageContent>
                        <MessageMetadata $type="user">
                          {formatMessageTime(group.userMessage.timestamp)}
                        </MessageMetadata>
                      </Message>
                    )}

                    {/* Active bot response - rendered in original message format */}
                    {group.botResponses.map((response) => {
                      // Show this response if it's the active tab, or if there's only one response and no tabs, or if it's a welcome message
                      // When viewing a loaded thread, show the active tab or first response if no tab is selected
                      const shouldShow =
                        response.isWelcome ||
                        (group.botResponses.length === 1 &&
                          getAllReaskOptions().length === 0 &&
                          !isViewingLoadedThread) ||
                        (isViewingLoadedThread &&
                          (String(activeTabs[group.id]) ===
                            String(response.id) ||
                            (!activeTabs[group.id] &&
                              group.botResponses.indexOf(response) === 0))) ||
                        (!isViewingLoadedThread &&
                          String(activeTabs[group.id]) === String(response.id));

                      if (!shouldShow) return null;

                      const preservedHeight = messageHeights[group.id];
                      const preservedWidth = messageWidths[group.id];

                      return (
                        <Message key={response.id} $type="bot">
                          <MessageContent
                            $type="bot"
                            ref={(el) => {
                              if (el) {
                                messageContentRefs.current[group.id] = el;
                              }
                            }}
                            style={{
                              ...(preservedHeight && {
                                minHeight: `${preservedHeight}px`,
                              }),
                              ...(preservedWidth && {
                                minWidth: `${preservedWidth}px`,
                              }),
                            }}
                          >
                            {/* Response tabs */}
                            {isViewingLoadedThread ? (
                              // Display-only tabs for loaded threads (like AdminThread)
                              (() => {
                                const uniqueModels = new Set();
                                const uniqueProviders = new Set();

                                group.botResponses.forEach((response) => {
                                  if (response.model)
                                    uniqueModels.add(response.model);
                                  if (response.provider)
                                    uniqueProviders.add(response.provider);
                                });

                                const hasMultipleModels =
                                  uniqueModels.size > 1 ||
                                  uniqueProviders.size > 1 ||
                                  group.botResponses.length > 1;

                                return (
                                  hasMultipleModels && (
                                    <AdminResponseTabs
                                      responses={group.botResponses}
                                      activeTabId={activeTabs[group.id]}
                                      onTabClick={handleTabSwitch}
                                      groupId={group.id}
                                    />
                                  )
                                );
                              })()
                            ) : (
                              // Interactive tabs for new messages (with reask functionality)
                              <MessagesResponseTabs
                                group={group}
                                activeTabId={activeTabs[group.id]}
                                onTabClick={(
                                  groupId,
                                  tabId,
                                  isReaskOption,
                                  reaskOption,
                                  originalResponseId,
                                ) =>
                                  handleTabClick(
                                    groupId,
                                    tabId,
                                    isReaskOption,
                                    reaskOption,
                                    originalResponseId,
                                    messageContentRefs.current[groupId],
                                  )
                                }
                                getAllReaskOptions={getAllReaskOptions}
                                selectedBot={selectedBot}
                                providerModelOptions={REASK_MODEL_OPTIONS}
                                isStreaming={messages.some(
                                  (msg) => msg.streaming,
                                )}
                              />
                            )}
                            {(() => {
                              // Process content and citations from server data
                              const rawContent = response.content || "";
                              let processedContent = rawContent;
                              let parsedCitations = response.citations;

                              const parsed =
                                parseContentAndCitations(rawContent);
                              processedContent = parsed.content;

                              // Parse citations if found
                              if (parsed.citations) {
                                try {
                                  parsedCitations = JSON.parse(
                                    parsed.citations,
                                  );
                                } catch (citationError) {
                                  console.warn(
                                    "Failed to parse citations:",
                                    citationError,
                                  );
                                  parsedCitations = response.citations; // Fallback to original
                                }
                              } else if (typeof parsedCitations === "string") {
                                // Parse citations if they're in string format (fallback)
                                try {
                                  parsedCitations = JSON.parse(parsedCitations);
                                } catch (e) {
                                  console.warn("Failed to parse citations:", e);
                                  parsedCitations = [];
                                }
                              }

                              // Convert any remaining literal \n characters to actual newlines
                              processedContent = processedContent.replace(
                                /\\n/g,
                                "\n",
                              );

                              return (
                                <MessageText>
                                  {processMessageContent(
                                    processedContent,
                                    parsedCitations,
                                    response.id,
                                    response.citationsLoading,
                                    response.model,
                                  )}
                                  {response.streaming && !processedContent && (
                                    <TypingIndicator>
                                      <span></span>
                                      <span></span>
                                      <span></span>
                                    </TypingIndicator>
                                  )}
                                </MessageText>
                              );
                            })()}
                          </MessageContent>
                          {(!response.streaming ||
                            response.responseComplete ||
                            response.provider ||
                            response.model) && (
                            <MessageMetadata $type="bot">
                              {formatMessageTime(response.timestamp)}
                              {/* Hide model metadata for welcome messages only */}
                              {!response.isWelcome && (
                                <>
                                  {response.provider && response.model
                                    ? ` • ${response.provider} • ${response.model}`
                                    : response.model || response.provider
                                      ? ` • ${
                                          response.model || response.provider
                                        }`
                                      : ""}
                                </>
                              )}
                            </MessageMetadata>
                          )}

                          {/* Rating container for bot messages that have content ready */}
                          {(!response.streaming || response.responseComplete) &&
                            response.content &&
                            !response.isWelcome &&
                            !response.citationsLoading && (
                              <RatingContainer>
                                <RatingButtons>
                                  <RatingButton
                                    $type="up"
                                    $selected={response.rating === "up"}
                                    onClick={() =>
                                      handleRating(response.id, "up")
                                    }
                                    title="Thumbs up"
                                  >
                                    👍
                                  </RatingButton>
                                  <RatingButton
                                    $type="down"
                                    $selected={response.rating === "down"}
                                    onClick={() =>
                                      handleRating(response.id, "down")
                                    }
                                    title="Thumbs down"
                                  >
                                    👎
                                  </RatingButton>
                                  {!response.feedback &&
                                    showFeedbackFor !== response.id && (
                                      <FeedbackButton
                                        onClick={() =>
                                          handleFeedbackToggle(response.id)
                                        }
                                        title="Add feedback"
                                      >
                                        Add feedback
                                      </FeedbackButton>
                                    )}
                                </RatingButtons>

                                {/* Show saved feedback when not editing */}
                                {response.feedback &&
                                  showFeedbackFor !== response.id && (
                                    <SavedFeedbackContainer>
                                      <SavedFeedbackDisplay>
                                        {response.feedback}
                                      </SavedFeedbackDisplay>
                                      <FeedbackActionButton
                                        onClick={() =>
                                          handleFeedbackEdit(response.id)
                                        }
                                        title="Edit feedback"
                                      >
                                        Edit
                                      </FeedbackActionButton>
                                    </SavedFeedbackContainer>
                                  )}

                                {/* Feedback input area when editing */}
                                {showFeedbackFor === response.id && (
                                  <>
                                    <FeedbackInput
                                      value={feedbackText}
                                      onChange={(e) =>
                                        setFeedbackText(e.target.value)
                                      }
                                      onKeyPress={(e) =>
                                        handleFeedbackKeyPress(e, response.id)
                                      }
                                      placeholder="Add your feedback about this response..."
                                      autoFocus
                                    />
                                    <FeedbackActions>
                                      <FeedbackActionButton
                                        onClick={handleFeedbackCancel}
                                      >
                                        Cancel
                                      </FeedbackActionButton>
                                      <FeedbackActionButton
                                        $primary
                                        onClick={() =>
                                          handleFeedbackSave(response.id)
                                        }
                                      >
                                        {response.feedback
                                          ? "Update"
                                          : "Submit"}
                                      </FeedbackActionButton>
                                    </FeedbackActions>
                                  </>
                                )}
                              </RatingContainer>
                            )}

                          {/* Citations container for structured responses */}
                          {(() => {
                            // Parse citations using same logic as content processing above
                            const rawContent = response.content || "";
                            let parsedCitations = response.citations;

                            // Use the same parseContentAndCitations function as above
                            const parsed = parseContentAndCitations(rawContent);

                            // Parse citations if found
                            if (parsed.citations) {
                              try {
                                parsedCitations = JSON.parse(parsed.citations);
                              } catch (citationError) {
                                console.warn(
                                  "Citations section: Failed to parse citations:",
                                  citationError,
                                );
                                parsedCitations = response.citations; // Fallback to original
                              }
                            } else if (typeof parsedCitations === "string") {
                              // Parse citations if they're in string format (fallback)
                              try {
                                parsedCitations = JSON.parse(parsedCitations);
                              } catch (e) {
                                console.warn("Failed to parse citations:", e);
                                parsedCitations = [];
                              }
                            }

                            return (
                              <Citations
                                citations={parsedCitations}
                                citationsLoading={response.citationsLoading}
                                responseId={response.id}
                                modelname={response.model}
                              />
                            );
                          })()}
                        </Message>
                      );
                    })}
                  </div>
                ))}

                {loading && messages.length === 0 && (
                  <Message $type="bot">
                    <MessageContent $type="bot">
                      <TypingIndicator>
                        <span></span>
                        <span></span>
                        <span></span>
                      </TypingIndicator>
                    </MessageContent>
                  </Message>
                )}
                <div ref={messagesEndRef} />
              </MessagesWrapper>
            </MessagesList>

            <MessageInputContainer $isEmbedded={isEmbedded}>
              {selectedFiles.length > 0 && (
                <SelectedFilesContainer>
                  {selectedFiles.map((file, index) => {
                    const error = getFileError(file);
                    return (
                      <SelectedFileChip key={`${file.name}-${index}`} $hasError={!!error}>
                        <span title={error ? `${file.name} - ${error}` : file.name}>
                          {file.name}
                          {error && <FileErrorText>({error})</FileErrorText>}
                        </span>
                        <button onClick={() => handleRemoveFile(index)}>×</button>
                      </SelectedFileChip>
                    );
                  })}
                  {selectedFiles.length > 1 && (
                    <FileCountBadge>({selectedFiles.length} files)</FileCountBadge>
                  )}
                </SelectedFilesContainer>
              )}
              <MessageInputWrapper $isEmbedded={isEmbedded}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  accept=".txt,.md,.json,.csv,.xlsx,.xls,.pdf,image/jpeg,image/png,image/gif,image/webp"
                  multiple
                />
                <FileInputButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || messages.some((msg) => msg.streaming)}
                  title="Attach file"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </FileInputButton>
                <MessageInput
                  ref={messageInputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={loading || messages.some((msg) => msg.streaming)}
                />
                <Button
                  onClick={sendMessage}
                  disabled={
                    !inputMessage.trim() ||
                    loading ||
                    messages.some((msg) => msg.streaming) ||
                    selectedFiles.some((f) => getFileError(f))
                  }
                  fullWidthOnMobile
                >
                  Send
                </Button>
              </MessageInputWrapper>
            </MessageInputContainer>
          </ChatContainer>
        </MessagesContainer>
      </MainContent>

      {!isEmbedded && (
        <>
          <SidePanel $isEmbedded={isEmbedded}>
            <ThreadList>
              {/* New Chat option */}
              <ThreadItem $active={!activeThreadId} onClick={handleNewChat}>
                <ThreadTitle>New chat</ThreadTitle>
              </ThreadItem>

              {/* Existing threads */}
              {threads.map((thread) => (
                <ThreadItem
                  key={thread.threadId}
                  $active={String(thread.threadId) === String(activeThreadId)}
                  onClick={() => handleThreadSelect(thread.threadId)}
                >
                  {editingThreadId === thread.threadId ? (
                    <ThreadTitleInput
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleTitleSave(thread.threadId);
                        } else if (e.key === "Escape") {
                          setEditingThreadId(null);
                          setEditingTitle("");
                        }
                      }}
                      onBlur={() => handleTitleSave(thread.threadId)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    <ThreadTitleRow>
                      <ThreadTitle>
                        {thread.title ||
                          `New Chat ${formatDateWithTime(thread.startedAt)}`}
                      </ThreadTitle>
                      <EditButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingThreadId(thread.threadId);
                          setEditingTitle(
                            thread.title ||
                              `New Chat ${formatDateWithTime(thread.startedAt)}`
                          );
                        }}
                        title="Rename thread"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                        </svg>
                      </EditButton>
                    </ThreadTitleRow>
                  )}
                  <ThreadDate>
                    {formatDateWithTime(thread.lastMessageAt)}
                  </ThreadDate>
                </ThreadItem>
              ))}
            </ThreadList>
          </SidePanel>
        </>
      )}
    </MobileWrapper>
  );
}

export default Messages;
